/**
 * «Аналитика» раздела EO «Виджет» (01.11, ТЗ 1.17 EMB-019 ч. 4, §0.6 п. 70): по сайту и периоду (сегодня, 7, 30 дней). Заявки по
 * видам, ставшие бронью, исходы POST — числа сервер считает ТОЙ ЖЕ функцией, что сводку 23:00 (OPS-006, apps/api v2Counts).
 * Сходятся они в периоде «Сводка 23:00» — то же окно, что у сводки (с 23:00 до 23:00); «Сегодня» — с полуночи, и заявки
 * с 23:00 до 24:00 там и в сводке разные (ревью b235d4e4: подпись говорит это прямо). Воронка — из сводок прокладки (открытия,
 * отправки, запасной путь, p75 tap_grid окон); lead_sent — честные заявки V4. Выбора слота в сводке нет — «нет данных». В выборе
 * — сайты с v2 и выбранный сайт без v2 (siteChoices), подпись совпадает с тем, чьи числа. Без ПДн.
 */
import { useState } from "react";
import { BASE, useLoad } from "./widgetApi";
import { KIND_LABEL, NO_DATA, numOr, orenTime, POST_LABEL, share, sumOf } from "./widgetFmt";
import { siteChoices } from "./widgetPick";
import { totalCounts, totalDiag } from "./widgetSum";
import type { Analytics, Overview, PeriodName } from "./widgetTypes";
import css from "./widget.module.css";

const PERIODS: { id: PeriodName; label: string }[] = [{ id: "today", label: "Сегодня" }, { id: "7d", label: "7 дней" }, { id: "30d", label: "30 дней" },
  { id: "summary", label: "Сводка 23:00" }];
const Cell = ({ k, v }: { k: string; v: string }) => <div className={css.cell}><span>{k}</span><b>{v}</b></div>;

export function WidgetAnalytics({ siteId, onSite }: { siteId: number | null; onSite: (id: number | null) => void }) {
  const [period, setPeriod] = useState<PeriodName>("today");
  const ov = useLoad<Overview>(`${BASE}/v2/overview`);
  const sites = siteChoices(ov.data?.sites ?? [], siteId);
  /* выбранного сайта нет среди пунктов (удалён) — «все сайты»: подпись выбора и числа — об одном */
  const sid = siteId !== null && ov.data && !sites.some((s) => s.id === siteId) ? null : siteId;
  const an = useLoad<Analytics>(`${BASE}/v2/analytics?period=${period}&site=${sid ?? "all"}`);
  const d = an.data;
  const v4 = d?.v4 ? (sid === null ? totalCounts(d.v4) : d.v4[0] ?? null) : null;
  const diag = d ? totalDiag(d.diag.sites) : null, windows = d ? sumOf(d.diag.windows) : 0;
  const submits = diag ? sumOf(diag.submit) : 0, fallbacks = diag ? sumOf(diag.fallback) : 0;
  return (
    <div className={css.card}>
      <div className={css.head}>
        <span className={css.title}>Аналитика</span>
        <div className={css.line}>
          {PERIODS.map((p) => <button key={p.id} type="button" className={`${css.stab} ${period === p.id ? css.stabOn : ""}`} aria-pressed={period === p.id} onClick={() => setPeriod(p.id)}>{p.label}</button>)}
          <select className={css.select} value={sid ?? ""} aria-label="Сайт" onChange={(e) => onSite(e.target.value ? Number(e.target.value) : null)}>
            <option value="">все сайты</option>
            {sites.map((s) => <option key={s.id} value={s.id}>{s.domain}{s.v2 ? "" : " — v2 не настроен"}</option>)}
          </select>
          <button type="button" className={css.ghost} disabled={an.busy} onClick={an.reload}>Обновить</button>
        </div>
      </div>
      {an.err ? <div className={css.err}>{an.err}</div> : null}
      {d ? <span className={css.muted}>{orenTime(d.from)} — {orenTime(d.to)} · заявки и исходы считает та же функция, что сводку 23:00 (OPS-006){d.period === "summary"
        ? ": окно то же, числа равны числам сообщения" : ". Сводка считает сутки с 23:00 до 23:00, а этот период — с 00:00: для сверки со сводкой — «Сводка 23:00»"}</span> : null}
      {d ? (
        <>
          <div className={css.sub}>Воронка</div>
          <div className={css.grid}>
            <Cell k="открытия виджета" v={diag ? String(diag.open) : NO_DATA} />
            <Cell k="выбор слота" v={NO_DATA} />
            <Cell k="отправки формы" v={diag ? String(submits) : NO_DATA} />
            <Cell k="lead_sent (честные заявки V4)" v={v4 ? String(v4.tracked) : NO_DATA} />
            <Cell k="доля запасного пути" v={diag ? share(fallbacks, diag.open) : NO_DATA} />
            <Cell k="p75 tap_grid (окна: среднее / худшее)" v={diag ? `${numOr(diag.tap_p75_avg, " мс")} / ${numOr(diag.tap_p75_max, " мс")}` : NO_DATA} />
          </div>
          {!windows ? <span className={css.muted}>Сводок прокладки за период нет — воронка: {NO_DATA}</span> : <span className={css.muted}>сводок прокладки за период: {windows}</span>}
          {diag && submits ? <div className={css.row}>Итоги отправок (виджет): {Object.entries(diag.submit).map(([k, n]) => `${k} — ${n}`).join(" · ")}</div> : null}
          {diag && fallbacks ? <div className={css.row}>Запасной путь по причинам: {Object.entries(diag.fallback).map(([k, n]) => `${k} — ${n}`).join(" · ")}</div> : null}
          <div className={css.sub}>Заявки (V4)</div>
          {!v4 ? <div className={css.muted}>{NO_DATA}: заявок v2 ещё не было</div> : (
            <>
              <div className={css.grid}>
                <Cell k="заявок" v={String(v4.requests)} />
                {Object.entries(v4.kinds).map(([k, n]) => <Cell key={k} k={KIND_LABEL[k] ?? k} v={String(n)} />)}
                <Cell k="стали бронью" v={`${v4.booked} (${share(v4.booked, v4.requests)})`} />
              </div>
              <div className={css.sub}>Исходы POST (V4)</div>
              <div className={css.grid}>
                {Object.entries(v4.post).map(([k, n]) => <Cell key={k} k={POST_LABEL[k] ?? k} v={`${n}${k === "201" && v4.hp ? ` (ловушка ${v4.hp})` : ""}`} />)}
              </div>
            </>
          )}
          {d.diag.post_routes ? <div className={css.row}>Ответы прокладки на POST /requests (все сайты): {Object.entries(d.diag.post_routes).map(([k, n]) => `${k} — ${n}`).join(" · ") || NO_DATA}</div> : null}
          {sid === null && d.v4 ? (
            <div className={css.tableWrap}>
              <table className={css.table}>
                <thead><tr><th>сайт</th><th className={css.num}>заявок</th><th className={css.num}>бронь</th><th className={css.num}>201</th><th className={css.num}>409/повтор</th><th className={css.num}>429</th><th className={css.num}>503</th></tr></thead>
                <tbody>{d.v4.map((s) => (
                  <tr key={s.id}><td>{s.domain}</td><td className={css.num}>{s.requests}</td><td className={css.num}>{s.booked}</td><td className={css.num}>{s.post["201"]}</td>
                    <td className={css.num}>{s.post["409"]}</td><td className={css.num}>{s.post["429"]}</td><td className={css.num}>{s.post["503"]}</td></tr>))}
                </tbody>
              </table>
            </div>
          ) : null}
        </>
      ) : !an.err ? <div className={css.muted}>Загружаю…</div> : null}
    </div>
  );
}
